import { Clock, Effect, Result, Schema as S } from "effect";

import { defineApi, staticApis } from "../client";
import type { ApiError } from "../errors";
import {
	type AgentsData,
	ApprovedSend,
	ContextsResponse,
	CreatedLink,
	InboxResponse,
	LinksResponse,
	PendingResponse,
	PromoteTarget,
	ReachableResponse,
	SentResponse,
} from "../schemas/agents";
import { listApiKeys } from "./integrations";

export const SENT_LIMIT = 6;

export const {
	getAgentInbox,
	getSharedContexts,
	getAgentLinks,
	getReachableAgents,
	getPendingSends,
} = staticApis({
	getAgentInbox: ["GET", "/agents/inbox", InboxResponse],
	getSharedContexts: ["GET", "/agents/contexts", ContextsResponse],
	getAgentLinks: ["GET", "/agents/links", LinksResponse],
	getReachableAgents: ["GET", "/agents/reachable", ReachableResponse],
	getPendingSends: ["GET", "/agents/outbound/pending", PendingResponse],
});

/** Owner-and-preview-only on the server; the URL is in this response alone. */
export const createAgentLink = defineApi("CreateAgentLink", {
	args: { agentId: S.String, label: S.String, forRecordId: S.NullOr(S.String) },
	response: CreatedLink,
	request: (body) => ({ method: "POST", path: "/agents/links", body }),
});

export const revokeAgentLink = defineApi("RevokeAgentLink", {
	args: { id: S.String },
	response: S.Unknown,
	request: ({ id }) => ({
		method: "DELETE",
		path: `/agents/links/${encodeURIComponent(id)}`,
	}),
});

export const denyPendingSend = defineApi("DenyPendingSend", {
	args: { id: S.String },
	response: S.Unknown,
	request: ({ id }) => ({
		method: "POST",
		path: `/agents/outbound/pending/${encodeURIComponent(id)}/deny`,
	}),
});

/** Sends the held message now; the body says whether it went out. */
export const approvePendingSend = defineApi("ApprovePendingSend", {
	args: { id: S.String },
	response: ApprovedSend,
	request: ({ id }) => ({
		method: "POST",
		path: `/agents/outbound/pending/${encodeURIComponent(id)}/approve`,
	}),
});

/** Link management is owners-only in preview: a 403 reads as "no links", not a failed page. */
const forbiddenAsEmpty = Result.orElse((error: ApiError) =>
	error._tag === "Http" && error.status === 403
		? Result.succeed({ links: [] })
		: Result.fail(error),
);

export const getAgentSent = defineApi("GetAgentSent", {
	args: { limit: S.Number },
	response: SentResponse,
	request: ({ limit }) => ({
		method: "GET",
		path: "/agents/sent",
		query: { limit: String(limit) },
	}),
});

/** Owner-only on the server (403 otherwise); callers refetch, so the body is not decoded. */
export const promoteContext = defineApi("PromoteContext", {
	args: { contextId: S.String, target: PromoteTarget },
	response: S.Unknown,
	request: ({ contextId, target }) => ({
		method: "POST",
		path: `/agents/contexts/${encodeURIComponent(contextId)}/promote`,
		body: { target },
	}),
});

export const revokeContext = defineApi("RevokeContext", {
	args: { contextId: S.String },
	response: S.Unknown,
	request: ({ contextId }) => ({
		method: "DELETE",
		path: `/agents/contexts/${encodeURIComponent(contextId)}`,
	}),
});

/** Inbox, sent feed, contexts and key names in parallel, plus the Clock; any failure fails the whole. */
export const fetchAgents = Effect.all(
	{
		inbox: getAgentInbox.run({}),
		sent: getAgentSent.run({ limit: SENT_LIMIT }),
		contexts: getSharedContexts.run({}),
		keys: listApiKeys.run({}),
		links: getAgentLinks.run({}).pipe(Effect.map(forbiddenAsEmpty)),
		reachable: getReachableAgents.run({}),
		pending: getPendingSends.run({}),
		now: Clock.currentTimeMillis.pipe(Effect.map(Result.succeed)),
	},
	{ concurrency: "unbounded" },
).pipe(
	Effect.map((parts) =>
		Result.map(
			Result.all(parts),
			({
				inbox,
				sent,
				contexts,
				keys,
				links,
				reachable,
				pending,
				now,
			}): AgentsData => ({
				now,
				inbox: inbox.inbox,
				sent: sent.sent,
				contexts: contexts.contexts,
				expiresNext: contexts.expiresNext,
				keys: keys.data,
				links: links.links,
				reachable: reachable.reachable,
				pendingSends: pending.pending,
			}),
		),
	),
);
